/**
 * Tiny notification "ding" synthesized via the Web Audio API.
 *
 * Why synth instead of an MP3?
 *  - Zero asset download / bundle bloat
 *  - No CORS / favicon-style cache headaches
 *  - Pleasant 2-tone chime that's distinguishable from system sounds
 *
 * Browser autoplay rules require a user gesture before any audio plays.
 * We lazy-create the AudioContext on the first call to `playNotifyDing()`
 * — typically that first call happens after the user clicks/taps something
 * in the app, so playback is permitted.
 */
let _ctx = null;

const getCtx = () => {
  if (_ctx) return _ctx;
  const AC = window.AudioContext || window.webkitAudioContext;
  if (!AC) return null;
  _ctx = new AC();
  return _ctx;
};

const playTone = (ctx, freq, startAt, duration, gain = 0.18) => {
  const osc = ctx.createOscillator();
  const g = ctx.createGain();
  osc.type = 'sine';
  osc.frequency.setValueAtTime(freq, startAt);
  g.gain.setValueAtTime(0, startAt);
  g.gain.linearRampToValueAtTime(gain, startAt + 0.01);
  g.gain.exponentialRampToValueAtTime(0.0001, startAt + duration);
  osc.connect(g).connect(ctx.destination);
  osc.start(startAt);
  osc.stop(startAt + duration);
};

export const playNotifyDing = () => {
  try {
    const ctx = getCtx();
    if (!ctx) return;
    // Some browsers (Safari) start the context in 'suspended' state until a
    // gesture; resume() is a no-op if already running.
    if (ctx.state === 'suspended') ctx.resume().catch(() => {});
    const t = ctx.currentTime;
    // Pleasant ascending two-tone ding ~ 200ms total.
    playTone(ctx, 880, t, 0.12);          // A5
    playTone(ctx, 1320, t + 0.08, 0.16);  // E6
  } catch {
    // Silent failure — audio is a "nice to have", never block UI.
  }
};
